/**
 * Página de detalle de un inventario: decide su propio modo (conteo o
 * resultado) según el estado del inventario y quién lo mira. Sirve tanto a
 * /inventarios/:id (admin) como a /mis-inventarios/:id (preparador).
 */
import React, { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Card, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import AuthService from "../../../services/auth.service";
import Roles from "../../../config/roles";
import { useDetalleInventario, useRevisarInventario } from "../hooks";
import { DetalleInventarioResultado, CargandoInventario } from "../components/ui";
import { ModalRevisarInventario } from "../components/modals";
import { INVENTARIO_ESTADO, ESTADOS_FINALIZADOS } from "../constants";
import ConteoInventario from "./ConteoInventario";
import { BOTON_SECUNDARIO_SX } from "../../../components/ui/buttonStyles";

/**
 * DECISION (TASK-016, tasks/milestones.md decisión 7): una sola página sirve
 * tanto al admin como al preparador, para no duplicar layout entre una
 * página de "resultado admin" y otra de "conteo preparador". El modo se
 * decide así: CONTEO si el inventario NO está finalizado (ni COMPLETADO ni
 * REVISADO) Y el usuario autenticado es el preparador asignado; RESULTADO de
 * solo lectura en cualquier otro caso (incluido un admin mirando un
 * inventario EN_CURSO ajeno, o el propio preparador tras finalizarlo).
 *
 * DECISION: en modo conteo se delega el render completo en ConteoInventario
 * (el mismo componente que ya servía '/mis-inventarios/:id' antes de esta
 * tarea, ver routes.js), aceptado a propósito para no duplicar ~200 líneas de
 * UI de conteo. Se le pasa `inventarioMostrado` como `inventarioInicial`
 * (limpieza post-review, TASK-019): su propio useDetalleInventario compara el
 * id de ese inventario con el de la URL y, si coincide, se ahorra el segundo
 * GET que antes repetía sin necesidad (ver DECISION en
 * hooks/useDetalleInventario.js).
 */
const DetalleInventario = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

    const { inventario, loading, error } = useDetalleInventario(id);

    // Revisar es una acción de admin, fuera de useDetalleInventario: su resultado se guarda aparte
    const [inventarioTrasRevisar, setInventarioTrasRevisar] = useState(null);
    const inventarioMostrado = inventarioTrasRevisar || inventario;
    const revision = useRevisarInventario({ inventario: inventarioMostrado, onRevisado: setInventarioTrasRevisar });

    const rutaVolver = location.pathname.startsWith("/mis-inventarios") ? "/mis-inventarios" : "/inventarios";
    const volver = useCallback(() => navigate(rutaVolver), [navigate, rutaVolver]);

    const usuarioActual = AuthService.getCurrentUser();
    const usuarioActualId = usuarioActual?.id;
    const esAdmin = Boolean(usuarioActual?.roles?.includes(Roles.ADMIN.role));

    const modoConteo = Boolean(
        inventarioMostrado &&
            !ESTADOS_FINALIZADOS.includes(inventarioMostrado.estado) &&
            usuarioActualId != null &&
            inventarioMostrado.preparadorId === usuarioActualId
    );

    // El modo no se sabe hasta tener el inventario: se titula por la ruta de entrada
    if (loading) {
        return <CargandoInventario titulo={rutaVolver === "/mis-inventarios" ? "Contar inventario" : "Detalle inventario"} />;
    }

    if (error || !inventarioMostrado) {
        return (
            <Stack spacing={2} sx={{ width: "100%" }}>
                <Alert severity="error">{error || "No se pudo cargar el inventario solicitado."}</Alert>
                <Button variant="outlined" onClick={volver} sx={{ ...BOTON_SECUNDARIO_SX, alignSelf: "flex-start" }}>
                    Volver
                </Button>
            </Stack>
        );
    }

    if (modoConteo) return <ConteoInventario rutaVolver={rutaVolver} inventarioInicial={inventarioMostrado} />;

    return (
        <Box sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <FactCheckIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        Detalle inventario
                    </Typography>
                </Box>

                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    <DetalleInventarioResultado
                        inventario={inventarioMostrado}
                        onRevisar={
                            esAdmin && inventarioMostrado.estado === INVENTARIO_ESTADO.COMPLETADO
                                ? revision.abrir
                                : undefined
                        }
                    />
                    <Button variant="outlined" onClick={volver} sx={{ ...BOTON_SECUNDARIO_SX, mt: 3 }}>
                        Volver
                    </Button>
                </Box>
            </Card>

            <ModalRevisarInventario
                show={revision.abierto}
                onHide={revision.cerrar}
                onConfirm={revision.confirmar}
                inventario={inventarioMostrado}
                loading={revision.revisando}
            />
        </Box>
    );
};

export default DetalleInventario;
